import { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { client, PersistentNotification } from '../../app/services/api/orderManagementClient';
import { UserContext } from './useUser';

const notificationRefreshIntervalMs = 60_000;

interface CachedNotifications {
  notifications: PersistentNotification[];
  fetchedAt: number;
}

// Cache is scoped to user and store. Entries expire so newly published
// notifications appear without requiring a full page reload.
const notificationsByStore = new Map<string, CachedNotifications>();

const isSameNotification = (first: PersistentNotification, second: PersistentNotification) =>
  first.type === second.type &&
  first.sourceId === second.sourceId &&
  first.revision === second.revision;

const getNotificationKey = (notification: PersistentNotification) =>
  `${notification.type}:${notification.sourceId}:${notification.revision}`;

const notificationOrder: Record<PersistentNotification['type'], number> = {
  Urgent: 0,
  OutOfStock: 1,
  News: 2,
};

const notificationColours: Record<PersistentNotification['type'], {
  backgroundColor: string;
  accentColor: string;
}> = {
  Urgent: { backgroundColor: '#fff7f7', accentColor: '#b42318' },
  OutOfStock: { backgroundColor: '#fffaf0', accentColor: '#b54708' },
  News: { backgroundColor: '#f4fbf6', accentColor: '#027a48' },
};

export const getPlainTextNotificationBody = (body: string) => {
  const document = new DOMParser().parseFromString(body, 'text/html');
  document.body.querySelectorAll('br').forEach(lineBreak => lineBreak.replaceWith('\n'));
  document.body.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6').forEach(block => {
    block.append('\n');
  });

  return (document.body.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .join('\n');
};

interface PersistentNotificationDialogProps {
  onBlockingStateChange?: (isBlocking: boolean) => void;
}

const PersistentNotificationDialog = ({ onBlockingStateChange }: PersistentNotificationDialogProps) => {
  const { t } = useTranslation('common');
  const { selectedStore, user } = useContext(UserContext);
  const [notifications, setNotifications] = useState<PersistentNotification[]>([]);
  const [acknowledgingNotificationKey, setAcknowledgingNotificationKey] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [acknowledgementErrorKey, setAcknowledgementErrorKey] = useState<string>();
  const [retryCount, setRetryCount] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);
  const storeNumber = selectedStore?.storeNumber;
  const notificationCacheKey = storeNumber && (user?.id ?? user?.emailAddress)
    ? `${user.id ?? user.emailAddress}:${storeNumber}`
    : undefined;
  const activeNotificationCacheKey = useRef<string>();
  activeNotificationCacheKey.current = notificationCacheKey;
  const orderedNotifications = [...notifications].sort((first, second) =>
    notificationOrder[first.type] - notificationOrder[second.type]);

  useEffect(() => {
    onBlockingStateChange?.(Boolean(notificationCacheKey) && (isLoading || fetchError || notifications.length > 0));
  }, [fetchError, isLoading, notificationCacheKey, notifications.length, onBlockingStateChange]);

  useEffect(() => {
    setNotifications([]);
    setAcknowledgingNotificationKey(undefined);
    setIsLoading(Boolean(notificationCacheKey));
    setFetchError(false);
    setAcknowledgementErrorKey(undefined);
  }, [notificationCacheKey]);

  useEffect(() => {
    let active = true;

    // A user/store key is required before retrieval. Without it, a response
    // cannot be cached and a refresh would repeatedly re-enter this effect.
    if (!storeNumber || !notificationCacheKey) return () => { active = false; };

    const cachedNotifications = notificationsByStore.get(notificationCacheKey);
    const cacheIsFresh = cachedNotifications &&
      Date.now() - cachedNotifications.fetchedAt < notificationRefreshIntervalMs;

    if (cacheIsFresh && retryCount === 0) {
      setNotifications(cachedNotifications.notifications);
      setIsLoading(false);
      const nextRefreshDelay = notificationRefreshIntervalMs - (Date.now() - cachedNotifications.fetchedAt);
      const refreshTimer = window.setTimeout(() => {
        if (active) setRefreshCount(value => value + 1);
      }, Math.max(1, nextRefreshDelay));
      return () => {
        active = false;
        window.clearTimeout(refreshTimer);
      };
    }

    setIsLoading(true);
    client.getPersistentNotifications(storeNumber)
      .then((result) => {
        if (active && result.success) {
          setIsLoading(false);
          setFetchError(false);
          if (notificationCacheKey) {
            notificationsByStore.set(notificationCacheKey, {
              notifications: result.data,
              fetchedAt: Date.now(),
            });
          }
          setNotifications(result.data);
          setRetryCount(0);
          setRefreshCount(value => value + 1);
        }
        if (active && !result.success) {
          setIsLoading(false);
          setFetchError(true);
        }
      })
      .catch(() => {
        if (active) {
          setIsLoading(false);
          setFetchError(true);
        }
      });

    return () => { active = false; };
  }, [notificationCacheKey, refreshCount, retryCount, storeNumber]);

  const getTitle = (notification: PersistentNotification) => {
    if (notification.type === 'Urgent') return t('notificationUrgentActionTab');
    if (notification.type === 'OutOfStock') return t('notificationOutOfStockTab');
    return notification.title || t('notificationNewsTab');
  };

  const acknowledge = async (acknowledgedNotification: PersistentNotification) => {
    if (!storeNumber || !notificationCacheKey) return;

    const acknowledgedCacheKey = notificationCacheKey;
    const acknowledgedKey = getNotificationKey(acknowledgedNotification);

    setAcknowledgingNotificationKey(acknowledgedKey);
    setAcknowledgementErrorKey(undefined);

    try {
      const result = await client.acknowledgePersistentNotification(storeNumber, {
        type: acknowledgedNotification.type,
        sourceId: acknowledgedNotification.sourceId,
        revision: acknowledgedNotification.revision,
      });

      if (!result.success) {
        if (activeNotificationCacheKey.current === acknowledgedCacheKey) setAcknowledgementErrorKey(acknowledgedKey);
        return;
      }

      const cachedNotifications = notificationsByStore.get(acknowledgedCacheKey);
      const remainingNotifications = (cachedNotifications?.notifications ?? notifications).filter(
        notification => !isSameNotification(notification, acknowledgedNotification),
      );
      notificationsByStore.set(acknowledgedCacheKey, {
        notifications: remainingNotifications,
        fetchedAt: cachedNotifications?.fetchedAt ?? Date.now(),
      });

      if (activeNotificationCacheKey.current === acknowledgedCacheKey) {
        setNotifications(current => current.filter(
          notification => !isSameNotification(notification, acknowledgedNotification),
        ));
      }
    } catch {
      if (activeNotificationCacheKey.current === acknowledgedCacheKey) setAcknowledgementErrorKey(acknowledgedKey);
    } finally {
      if (activeNotificationCacheKey.current === acknowledgedCacheKey) setAcknowledgingNotificationKey(undefined);
    }
  };

  return (
    <>
      {notifications.length > 0 && (
        <Stack spacing={0.5}
          aria-label={t('notificationTitle')}
          sx={{
            mb: 1.5,
            flexShrink: 0,
            width: 'calc(100% - 10px)',
            minWidth: 0,
          }}>
          {orderedNotifications.map(notification => {
            const notificationKey = getNotificationKey(notification);
            const colours = notificationColours[notification.type];
            const isAcknowledging = acknowledgingNotificationKey === notificationKey;
            const NotificationIcon = notification.type === 'Urgent' ? ErrorOutlineIcon : InfoOutlinedIcon;

            return (
              <Box
                component="section"
                data-testid="persistent-notification"
                key={notificationKey}
                sx={{
                  display: 'flex',
                  position: 'relative',
                  minHeight: 68,
                  width: '100%',
                  minWidth: 0,
                  flexShrink: 0,
                  bgcolor: colours.backgroundColor,
                  border: 1,
                  borderColor: '#e4e7ec',
                  borderRadius: 1.5, 
                  height: 'auto',
                  //minHeight: 68,
                  //flexShrink: 0,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(16, 24, 40, 0.12)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: '0 auto 0 0',
                    width: 6,
                    bgcolor: colours.accentColor,
                    borderRadius: '6px 0 0 6px',
                  },
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '4px',
                  width: '100%',
                  padding: '10px 8px',
                  boxSizing: 'border-box',
                }}>
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 20, height: 20, flexShrink: 0 }}>
                    <NotificationIcon sx={{ color: colours.accentColor, fontSize: 20 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                      <Typography component="h2"
                        variant="textXS"
                        sx={{ fontSize: '0.8125rem', lineHeight: 1.1, flex: 1, minWidth: 0 }}
                        fontWeight={700}
                        color={colours.accentColor}>
                        {getTitle(notification)}
                      </Typography>
                      <Tooltip title={t('notificationAcknowledge')}>
                        <span>
                          <IconButton
                            aria-label={t('notificationAcknowledge')}
                            onClick={() => acknowledge(notification)}
                            disabled={isAcknowledging}
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              mt: -0.5,
                              mr: -0.5,
                              color: '#344054',
                              bgcolor: 'transparent',
                              '&:hover': { bgcolor: '#f2f4f7' },
                            }}
                          >
                            <CheckIcon sx={{ fontSize: 24 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Typography
                      component="div"
                      variant="textXS"
                      sx={{
                        mt: 0.25,
                        lineHeight: '16px',
                        maxHeight: '80px', // 5 lines at 16px
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollbarWidth: 'auto',
                        '&::-webkit-scrollbar': { width: '2px' },
                        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: 0,
                        },
                        '@supports not selector(::-webkit-scrollbar)': {
                          scrollbarWidth: 'thin',
                          scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                        },
                        color: '#344054',
                      }}
                    >
                      {getPlainTextNotificationBody(notification.body)}
                    </Typography>
                    {acknowledgementErrorKey === notificationKey && <Typography variant="textXS"
                      sx={{ display: 'block', mt: 0.25, color: colours.accentColor }}>
                      {t('notificationErrorAcknowledging')}
                    </Typography>}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
      <Dialog
        open={fetchError && notifications.length === 0}
        disableEscapeKeyDown
        aria-labelledby='persistent-notification-title'
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle id='persistent-notification-title'>
         s {t('notificationTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity='error'>{t('notificationErrorFetchingData')}</Alert>
        </DialogContent>
        <DialogActions>
          <Button variant='primary'
            onClick={() => {
              setFetchError(false);
              setRetryCount((value) => value + 1);
            }}>
            {t('notificationRetry')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PersistentNotificationDialog;
